"use client";

import { useRef, useState } from "react";
import { Link2, MousePointerClick, QrCode, Route } from "lucide-react";
import {
  CODE,
  CornerRibbon,
  GradText,
  SectionHead,
  SectionShell,
  Terminal,
  useBandRowAlign,
  useRefGridMode,
  useRibbonAnchor,
  useWholeRowBlocks,
} from "./kit";
import { gsap, NOT_REDUCED, useGSAP } from "./kit/motion";
import { roadmapItem } from "@/lib/roadmap";

/** Outbound webhooks are a roadmap item (lib/roadmap.ts) — the date comes
 *  from there, never typed here. Shipping them is a `shipped` edit there
 *  plus a rewrite of this section. */
const WEBHOOKS_ETA = roadmapItem("webhooks").promised;

/** What exists today: `event: click` frames on GET /v1/events/stream
 *  (backend routers/public_v1.py; payload from request_log_consumer.py) —
 *  human clicks on links created through the public API only. */
const EVENTS = [
  { id: "social", type: "event: click", detail: "DE · mobile · via instagram.com", time: "just now", Icon: MousePointerClick },
  { id: "qr", type: "event: click", detail: "US · desktop · QR scan (is_qr: true)", time: "2 s ago", Icon: QrCode },
  { id: "utm", type: "event: click", detail: "FR · mobile · utm_source=newsletter", time: "14 s ago", Icon: Link2 },
  { id: "rule", type: "event: click", detail: 'DE · desktop · rule geo.country == "DE"', time: "31 s ago", Icon: Route },
] as const;

const SNIPPET = (
  <>
    <span className={CODE.comment}>{"# Live today: human clicks on your API-created links"}</span>
    {"\n"}$ curl -N https://api.rii.link/v1/events/stream \
    {"\n"}    -H <span className={CODE.string}>&quot;Authorization: Bearer $RII_KEY&quot;</span>
    {"\n"}
    {"\n"}<span className={CODE.comment}>: connected</span>
    {"\n"}
    {"\n"}<span className={CODE.key}>event:</span> click
    {"\n"}<span className={CODE.key}>data:</span> {"{"}<span className={CODE.string}>&quot;link_id&quot;</span>:<span className={CODE.string}>&quot;lnk_8h2f0qZ3Tk1mR4vXw9aBcQ&quot;</span>,
    {"\n"}       <span className={CODE.string}>&quot;country&quot;</span>:<span className={CODE.string}>&quot;DE&quot;</span>,<span className={CODE.string}>&quot;device&quot;</span>:<span className={CODE.string}>&quot;mobile&quot;</span>,
    {"\n"}       <span className={CODE.string}>&quot;referrer&quot;</span>:<span className={CODE.string}>&quot;instagram.com&quot;</span>,<span className={CODE.string}>&quot;is_qr&quot;</span>:<span className={CODE.number}>false</span>, …{"}"}
    {"\n"}
    {"\n"}<span className={CODE.comment}>{`# Outbound webhooks (push to your endpoint): ${WEBHOOKS_ETA}`}</span>
  </>
);

const SR_SUMMARY =
  `Outbound webhooks are on the roadmap for ${WEBHOOKS_ETA} and are not available yet. ` +
  "What works today is the live server-sent event stream, GET /v1/events/stream: one click event per human " +
  "click on links created through the public API. Example events: a mobile click from Germany via " +
  "instagram.com, a desktop QR scan from the US, a mobile click from France tagged utm_source=newsletter, " +
  'and a desktop click from Germany that matched the rule geo.country == "DE".';

/**
 * WebhooksSection — light supporting-cast section. Outbound webhooks are
 * ROADMAP (amber corner sash + dated lead, both from lib/roadmap.ts); the
 * visuals show only what exists today — the SSE click stream — so nothing
 * here looks like a live webhook. The feed rows arrive one by one (finite
 * pass, completion-chained restart, IO-gated — the Switchboard idiom).
 * Static / reduced-motion / mobile fallback: the feed fully populated.
 */
export default function WebhooksSection() {
  const [enhanced, setEnhanced] = useState(false);
  const layoutRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const holdRef = useRef<number>(0);
  const visibleRef = useRef(false);
  const onGrid = useRefGridMode();
  const ribbonWrapRef = useRef<HTMLDivElement>(null);
  const ribbonPos = useRibbonAnchor(ribbonWrapRef, onGrid);

  useWholeRowBlocks(layoutRef);
  useBandRowAlign(layoutRef);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        setEnhanced(true);
        const q = gsap.utils.selector(feedRef);
        gsap.set(q("[data-event-row]"), { autoAlpha: 0, y: 10 });

        const tl = gsap.timeline({
          paused: true,
          defaults: { ease: "power3.out", immediateRender: false },
        });
        tl.set(q("[data-event-row]"), { autoAlpha: 0, y: 10 });
        EVENTS.forEach((e, i) => {
          tl.to(q(`[data-event-row="${e.id}"]`), { autoAlpha: 1, y: 0, duration: 0.45 }, i === 0 ? "+=0.2" : "+=0.9")
            .fromTo(
              q(`[data-event-row="${e.id}"]`),
              { boxShadow: "0 0 0 1px rgba(250,67,127,0)" },
              { boxShadow: "0 0 0 1px rgba(250,67,127,0.45)", duration: 0.3, yoyo: true, repeat: 1, ease: "power2.inOut" },
            );
        });
        tl.to({}, { duration: 3.5 });

        const scheduleReplay = () => {
          window.clearTimeout(holdRef.current);
          holdRef.current = window.setTimeout(() => {
            if (visibleRef.current) tl.restart();
          }, 60);
        };
        tl.eventCallback("onComplete", scheduleReplay);

        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              visibleRef.current = e.isIntersecting;
              if (!e.isIntersecting) {
                tl.pause();
                return;
              }
              if (tl.progress() === 1) tl.restart();
              else tl.play();
            }),
          { threshold: 0.3 },
        );
        if (feedRef.current) io.observe(feedRef.current);

        return () => {
          gsap.set(q("[data-event-row]"), { clearProps: "opacity,visibility,transform" });
          io.disconnect();
          window.clearTimeout(holdRef.current);
          tl.kill();
          setEnhanced(false);
        };
      });
    },
    { scope: feedRef },
  );

  return (
    <SectionShell id="ref-webhooks">
      {/* Roadmap sash at the title box's corner (same anchor as McpSection). */}
      <div ref={ribbonWrapRef} className="relative">
        <CornerRibbon
          label={`Coming ${WEBHOOKS_ETA}`}
          style={{ top: ribbonPos.top, right: ribbonPos.right }}
        />
        <SectionHead
          eyebrow="Webhooks · roadmap"
          title={
            <>
              Your stack will hear <GradText>every click.</GradText>
            </>
          }
          lead={`Outbound webhooks — push delivery to your own endpoint — are on the roadmap for ${WEBHOOKS_ETA}. Live today: a server-sent click stream for your API-created links, one curl -N away.`}
        />
      </div>

      <div ref={layoutRef} data-grid-block className="grid items-start gap-[46px] lg:grid-cols-[1fr_1.1fr]">
        <div className="min-w-0">
          <p className="sr-only">{SR_SUMMARY}</p>
          <div
            ref={feedRef}
            data-enhanced={enhanced || undefined}
            aria-hidden="true"
            className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref-lg"
          >
            <div className="border-b border-line bg-surface-2 px-[18px] py-[11px] font-jetbrains text-[11.5px] uppercase tracking-[0.08em] text-dim">
              GET /v1/events/stream
            </div>
            {EVENTS.map(({ id, type, detail, time, Icon }) => (
              <div
                key={id}
                data-event-row={id}
                className="flex items-center gap-3 rounded-[4px] border-b border-line px-[18px] py-3"
              >
                <Icon size={16} className="shrink-0 text-brand-600" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="font-jetbrains text-[13px] font-semibold text-ink">{type}</div>
                  <div className="truncate text-[12.5px] text-muted">{detail}</div>
                </div>
                <span className="ml-auto shrink-0 font-jetbrains text-[11.5px] text-dim">{time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="min-w-0">
          <Terminal path="stream-clicks.sh" className="h-full">
            <pre className="m-0 whitespace-pre-wrap font-[inherit]">{SNIPPET}</pre>
          </Terminal>
        </div>
      </div>
    </SectionShell>
  );
}
